import { fmt, fmtDate, type Supplier360 } from '@dcc-erp/shared';

import type { CSSProperties } from 'react';

// Aging buckets reconciled to the REAL `Supplier360['agingBuckets']` keys.
const AGING: { key: keyof Supplier360['agingBuckets']; label: string; color: string }[] = [
  { key: 'current', label: 'ยังไม่ถึงกำหนด', color: 'var(--positive)' },
  { key: 'd1to30', label: '1-30', color: '#7aa64f' },
  { key: 'd31to60', label: '31-60', color: 'var(--warning)' },
  { key: 'd61to90', label: '61-90', color: '#d98324' },
  { key: 'over90', label: '90+', color: 'var(--negative)' },
];

export function Supplier360Panel({
  data,
  isLoading,
}: {
  data: Supplier360 | undefined;
  isLoading: boolean;
}) {
  if (isLoading || !data) {
    return (
      <div
        data-testid="s360-loading"
        className="card"
        style={{ padding: 14, color: 'var(--text-muted)' }}
      >
        กำลังโหลดภาพรวม…
      </div>
    );
  }
  const total = AGING.reduce((s, a) => s + (data.agingBuckets[a.key] || 0), 0) || 1;
  const stat: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 2 };
  return (
    <div className="card" style={{ padding: 14, display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <div style={stat}>
          <span className="meta">ยอดค้างชำระ (Open AP)</span>
          <b style={{ fontSize: 18 }}>{fmt(data.openApBalance, 2)}</b>
        </div>
        <div style={stat}>
          <span className="meta">ยอดซื้อสะสมปีนี้ (YTD)</span>
          <b style={{ fontSize: 18 }}>{fmt(data.ytdSpend, 2)}</b>
        </div>
        <div style={stat}>
          <span className="meta">ชำระล่าสุด</span>
          <b style={{ fontSize: 18 }}>{data.lastPaidAt ? fmtDate(data.lastPaidAt) : '—'}</b>
        </div>
      </div>

      <div>
        <div className="meta" style={{ marginBottom: 4 }}>
          อายุหนี้ค้างชำระ
        </div>
        <div
          style={{
            display: 'flex',
            height: 14,
            borderRadius: 7,
            overflow: 'hidden',
            background: '#f1f1f4',
          }}
        >
          {AGING.map((a) => {
            const v = data.agingBuckets[a.key] || 0;
            return v > 0 ? (
              <div
                key={a.key}
                title={`${a.label}: ${fmt(v, 2)}`}
                style={{ width: `${(v / total) * 100}%`, background: a.color }}
              />
            ) : null;
          })}
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
          {AGING.map((a) => (
            <span
              key={a.key}
              className="meta"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <i
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: 2,
                  background: a.color,
                  display: 'inline-block',
                }}
              />
              {a.label} {fmt(data.agingBuckets[a.key] || 0, 2)}
            </span>
          ))}
        </div>
      </div>

      <div>
        <div className="meta" style={{ marginBottom: 4 }}>
          เอกสารล่าสุด
        </div>
        {data.recentIngests.length === 0 ? (
          <div className="meta">— ยังไม่มีเอกสาร —</div>
        ) : (
          <table className="tbl" style={{ fontSize: 12.5, width: '100%' }}>
            <tbody>
              {data.recentIngests.map((d) => (
                <tr key={d.ingestId}>
                  <td>{d.fileName}</td>
                  <td>
                    <span className="badge neutral">{d.status}</span>
                  </td>
                  <td style={{ textAlign: 'right' }}>{fmtDate(d.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
